import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TABLES } from "../../../Apis/Tables";
import { permissionObj } from "../../../Helpers/permission";
import { SEARCH_MODEL } from "../../../components/table/Model/TableModel";
import { SimpleSearchWithId } from "../../../reduxToolkit/Search/SearchApi";
import { RESIDENTS_TABS_MODEL } from "../Model/Model";

// Only the total of the search is needed for the badge, the smallest page is
// asked for so the count does not bring the list of the tab twice
const COUNT_PAGE_SIZE = 1;

// Search model each tab counts with
const TAB_SEARCH_MODEL = {
    [RESIDENTS_TABS_MODEL.REGISTERED.key]: TABLES.BUILDING_APARTMENT_RESIDENT,
    [RESIDENTS_TABS_MODEL.WHITE_LIST.key]: TABLES.BUILDING_WHITE_LIST
};

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Residents tabs logic of the building detail: selected tab, the add to white
 * list modal and the totals painted on the badge of both tabs. The tab that is
 * rendered reports the total of its own list, only the one that is not mounted
 * is counted here, the first time the building arrives and again after the
 * modal saves.
 *
 * @param {Object} props
 * @param {string|number} props.buildingId - Building the residents belong to.
 * @param {Function} props.setModified - Flags the building detail to be read again,
 * the verification resume is counted by that endpoint.
 * @returns {Object} Tab state, badge counts and the handlers of the modal.
 */
export const ResidentsTabsApp = ({ buildingId, setModified }) => {
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    // Authorizing a resident on the white list is part of the residents management
    const canManageResidents = DEV || permission?.includes(permissionObj?.WEB_BUILDING_MANAGE_RESIDENTS);
    const [selectedTab, setSelectedTab] = useState(RESIDENTS_TABS_MODEL.REGISTERED.key);
    const [whiteListModal, setWhiteListModal] = useState(false);
    // Bumped every time the white list modal saves so both tabs reload their list
    const [refresh, setRefresh] = useState(0);
    const [counts, setCounts] = useState({});
    // Tab rendered when the counts run, read through a ref so switching tabs
    // does not count again, the list of the tab opened reports its own total
    const selectedTabRef = useRef(selectedTab);
    selectedTabRef.current = selectedTab;

    /**
     * Counts the residents of one tab, the search is the same one of its list
     * but asking for a single row, only the total reaches the badge.
     *
     * @param {string} tabKey - Key of the tab counted, of RESIDENTS_TABS_MODEL.
     */
    const getCount = useCallback((tabKey) => {
        const body = SEARCH_MODEL.setObject({
            page: 0,
            size: COUNT_PAGE_SIZE,
            model: TAB_SEARCH_MODEL[tabKey],
            userId: buildingId
        });

        dispatch(SimpleSearchWithId({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success !== true) return;

                setCounts((prev) => ({ ...prev, [tabKey]: data?.totalElements || 0 }));
            }
        );
    }, [buildingId, dispatch]);

    /**
     * Total reported by the list of the tab that is rendered, it lands on the
     * badge without asking the search again.
     *
     * @param {string} tabKey - Key of the tab reporting, of RESIDENTS_TABS_MODEL.
     * @param {number} total - Total elements of its search.
     */
    const handleTotalChange = useCallback((tabKey, total) =>
        setCounts((prev) => ({ ...prev, [tabKey]: total })), []);

    // The badge of the tab that is not rendered has no list behind it to fill
    // it, it is counted once the building detail arrives and again after the
    // white list modal saves
    useEffect(() => {
        if (!buildingId) return;

        Object.keys(TAB_SEARCH_MODEL)
            .filter((tabKey) => tabKey !== selectedTabRef.current)
            .forEach((tabKey) => getCount(tabKey));
    }, [buildingId, refresh, getCount]);

    /**
     * Reloads both lists and their counts after the white list modal saves, and
     * reads the building detail again so the verification resume counts the
     * residents that were just authorized.
     */
    const handleSaved = () => {
        setRefresh((prev) => prev + 1);
        setModified?.(true);
    };

    return {
        canManageResidents,
        selectedTab,
        setSelectedTab,
        whiteListModal,
        setWhiteListModal,
        refresh,
        counts,
        handleTotalChange,
        handleSaved
    };
};
